import React, { useState, useRef, useEffect } from 'react';
import { Form, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import AuthLayout from './AuthLayout';
import dummyProfile from '../../assets/images/avatar.png';
import brandLogo from '../../assets/images/logo.png';
import { registerUser, googleLoginUser, clearError } from '../../store/slices/authSlice';
import { getAllCountriesApi, getTimeZonesApi, sendSignupCodeApi, verifySignupCodeApi } from '../../api/auth';
import { loadGoogleScript, parseJwt } from '../../utils/googleAuth';

const SignUp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error, isAuthenticated } = useSelector((state) => state.auth);

  const [role, setRole] = useState('Student');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    experience: '',
    password: '',
    confirmPassword: '',
    birthDate: '',
    language: '',
    country: '',
    timezone: '',
    description: ''
  });

  const [countriesList, setCountriesList] = useState([]);
  const [timezonesList, setTimezonesList] = useState([]);
  const [languages] = useState(['English', 'Spanish', 'French', 'German', 'Chinese', 'Japanese', 'Hindi', 'Arabic']);

  const [profileImage, setProfileImage] = useState(null);
  const [coverImage, setCoverImage] = useState(null);
  const [profileFile, setProfileFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // OTP State
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState(null);
  const [otpSuccess, setOtpSuccess] = useState(null);

  const profileInputRef = useRef(null);
  const coverInputRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
    return () => {
      dispatch(clearError());
    };
  }, [isAuthenticated, navigate, dispatch]);

  const handleGoogleLoginResponse = async (response) => {
    if (response.credential) {
      const payload = parseJwt(response.credential);
      if (payload) {
        dispatch(googleLoginUser({
          displayName: payload.name || '',
          email: payload.email || '',
          id: payload.sub || '',
          photoUrl: payload.picture || '',
          user_type: role.toLowerCase(), // 'student' or 'teacher'
        }));
      }
    }
  };

  useEffect(() => {
    loadGoogleScript(() => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleGoogleLoginResponse,
        });
        window.google.accounts.id.renderButton(
          document.getElementById('google-signup-btn'),
          { theme: 'outline', size: 'large', type: 'standard', width: 320 }
        );
      }
    });
  }, [role]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [countriesRes, timezonesRes] = await Promise.all([
          getAllCountriesApi(),
          getTimeZonesApi()
        ]);
        if (countriesRes.status === 200) setCountriesList(countriesRes.data);
        if (timezonesRes.status === 200) setTimezonesList(timezonesRes.data);
      } catch (err) {
        console.error("Error fetching initial data:", err);
      }
    };
    fetchData();

    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (formErrors[name]) setFormErrors({ ...formErrors, [name]: '' });
  };

  const handlePhoneChange = (value) => {
    if (value.length <= 15) {
      setFormData({ ...formData, phone: value });
      if (formErrors.phone) setFormErrors({ ...formErrors, phone: '' });
    }
  };

  const handleImageChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (type === 'profile') setProfileFile(file);
      else setCoverFile(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'profile') setProfileImage(reader.result);
        else setCoverImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSendOTP = async () => {
    if (!formData.email) {
      setOtpError("Please enter your email first");
      return;
    }
    setOtpLoading(true);
    setOtpError(null);
    setOtpSuccess(null);
    try {
      const res = await sendSignupCodeApi({ email: formData.email });
      if (res.status === 200) {
        setOtpSent(true);
        setOtpSuccess("OTP sent to your email!");
      } else {
        setOtpError(res.message || "Failed to send OTP");
      }
    } catch (err) {
      setOtpError(err.response?.data?.message || "Error sending OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otpCode) {
      setOtpError("Please enter the OTP");
      return;
    }
    setOtpLoading(true);
    setOtpError(null);
    try {
      const res = await verifySignupCodeApi({ email: formData.email, code: otpCode });
      if (res.status === 200) {
        setOtpVerified(true);
        setOtpSuccess("Email verified successfully!");
      } else {
        setOtpError(res.message || "Invalid OTP");
      }
    } catch (err) {
      setOtpError(err.response?.data?.message || "Error verifying OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!otpVerified) {
      setOtpError("Please verify your email with OTP first");
      return;
    }

    const data = new FormData();
    data.append('full_name', formData.fullName);
    data.append('email', formData.email);
    data.append('password', formData.password);
    data.append('mobile', formData.phone);
    data.append('user_type', role.toLowerCase());
    data.append('dob', formData.birthDate);
    data.append('time_zone', formData.timezone);
    data.append('code', otpCode);
    data.append('description', formData.description);

    if (role === 'Teacher') {
      data.append('experience', formData.experience || 0);
    }

    if (formData.country) data.append('country', formData.country);
    if (formData.language) data.append('language', formData.language);

    if (profileFile) data.append('profile_picture', profileFile);
    if (coverFile) data.append('profile_cover_image', coverFile);

    try {
      const result = await dispatch(registerUser(data)).unwrap();
      if (result) {
        navigate('/login');
      }
    } catch (err) {
      console.error("Registration error:", err);
    }
  };

  const isFormValid = formData.fullName &&
    formData.email &&
    formData.phone &&
    formData.password.length >= 8 &&
    formData.password === formData.confirmPassword &&
    otpVerified &&
    (role !== 'Teacher' || formData.experience !== '');

  return (
    <AuthLayout
      showBrand={false}
      title={false}
      subtitle={false}
      wide={true}
    >
      <div className="wrapper text-center mt-n4 mb-3">
        <div className="profile-upload-section border border-3 border-primary rounded-circle position-relative shadow mx-auto"
          style={{ height: "100px", minWidth: "100px", width: "100px" }}
        >
          <Form.Control type="file" hidden ref={profileInputRef} onChange={(e) => handleImageChange(e, 'profile')} accept="image/*" />
          <img src={profileImage || dummyProfile} className="profile-image-preview rounded-circle w-100 h-100" alt="Profile" />
          <div className="upload-btn bg-primary position-absolute bottom-0 end-0 pointer rounded-circle d-flex align-items-center justify-content-center"
            onClick={() => profileInputRef.current.click()}
            style={{ width: "26px", height: "26px" }}
          >
            <i className="bi bi-camera text-sm text-white"></i>
          </div>
        </div>
      </div>

      <div className="wrapper d-flex flex-wrap align-items-center gap-2 mb-3">
        <Link to="/" className="brand-logo text-primary d-flex align-items-center justify-content-center gap-2 text-decoration-none">
          <img src={brandLogo} alt="Logo" className="img-fluid" style={{ height: "40px" }} />
        </Link>
        <div className="info-wrapper">
          <div className="title text-base text-gray-900 fw-bold">Create your account</div>
          <div className="subtitle text-sm text-gray-500">Complete your profile to get started</div>
        </div>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => dispatch(clearError())}>{error}</Alert>}
      {otpError && <Alert variant="danger" dismissible onClose={() => setOtpError(null)}>{otpError}</Alert>}
      {otpSuccess && <Alert variant="success" dismissible onClose={() => setOtpSuccess(null)}>{otpSuccess}</Alert>}

      <div className="role-selector d-flex gap-3 mb-3">
        <div className={`role-option ${role === 'Student' ? 'active' : ''}`} onClick={() => setRole('Student')}>Join as Student</div>
        <div className={`role-option ${role === 'Teacher' ? 'active' : ''}`} onClick={() => setRole('Teacher')}>Join as Teacher</div>
      </div>

      <Form onSubmit={handleSubmit}>
        <Row className="g-3 mb-3">
          <Col sm={6}>
            <Form.Group>
              <Form.Label>Full Name</Form.Label>
              <Form.Control type="text" name="fullName" placeholder="Enter your name" value={formData.fullName} onChange={handleInputChange} required />
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group>
              <Form.Label>Email Address</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={otpVerified}
                />
                {!otpVerified && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleSendOTP}
                    disabled={otpLoading || !formData.email}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {otpLoading ? <Spinner animation="border" size="sm" /> : (otpSent ? 'Resend' : 'Send OTP')}
                  </Button>
                )}
              </div>
            </Form.Group>
          </Col>
        </Row>

        {otpSent && !otpVerified && (
          <Row className="g-3 mb-3">
            <Col sm={12}>
              <Form.Group>
                <Form.Label>Enter Verification Code</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    maxLength={6}
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleVerifyOTP}
                    disabled={otpLoading || !otpCode}
                  >
                    {otpLoading ? <Spinner animation="border" size="sm" /> : 'Verify'}
                  </Button>
                </div>
              </Form.Group>
            </Col>
          </Row>
        )}

        <Row className="g-3 mb-3">
          <Col sm={6}>
            <Form.Group>
              <Form.Label>Mobile Number</Form.Label>
              <PhoneInput
                country={'in'}
                value={formData.phone}
                onChange={handlePhoneChange}
                placeholder="Enter mobile number"
                inputProps={{
                  required: true,
                  maxLength: 15
                }}
              />
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group>
              <Form.Label>Date of Birth</Form.Label>
              <Form.Control type="date" name="birthDate" value={formData.birthDate} onChange={handleInputChange} required />
            </Form.Group>
          </Col>
        </Row>

        {role === 'Teacher' && (
          <Row className="g-3 mb-3">
            <Col sm={12}>
              <Form.Group>
                <Form.Label>Years of Experience</Form.Label>
                <Form.Control
                  type="number"
                  name="experience"
                  min="0"
                  placeholder="Enter your experience in years"
                  value={formData.experience}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>
        )}

        <Row className="g-3 mb-3">
          {/* {role === 'Teacher' && ( */}
          <Col sm={6}>
            <Form.Group>
              <Form.Label>Your Current Language</Form.Label>
              <Form.Select name="language" value={formData.language} onChange={handleInputChange} required >
                <option value="" hidden>Select Language</option>
                {languages.map(lang => <option key={lang} value={lang}>{lang}</option>)}
              </Form.Select>
            </Form.Group>
          </Col>
          {/* )} */}
          <Col sm={6}>
            <Form.Group>
              <Form.Label>Select Country</Form.Label>
              <Form.Select name="country" value={formData.country} onChange={handleInputChange} required >
                <option value="" hidden>Select Country</option>
                {countriesList.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </Form.Select>
            </Form.Group>
          </Col>
          {/* <Col sm={role === 'Teacher' ? 12 : 6}>
            <Form.Group>
              <Form.Label>Select Timezone</Form.Label>
              <Form.Select name="timezone" value={formData.timezone} onChange={handleInputChange} required >
                <option value="" hidden>Select Timezone</option>
                {timezonesList.map(tz => <option key={tz._id} value={tz._id}>{tz.timezone}</option>)}
              </Form.Select>
            </Form.Group>
          </Col> */}
        </Row>

        <Row className="g-3 mb-3">
          <Col sm={6}>
            <Form.Group>
              <Form.Label>Create Password</Form.Label>
              <div className="password-icon-wrapper">
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Enter password"
                  value={formData.password}
                  onChange={handleInputChange}
                  maxLength={128}
                  required
                />
                <span className="eye-btn"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <i className="bi bi-eye-slash"></i> : <i className="bi bi-eye"></i>}
                </span>
              </div>
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group>
              <Form.Label>Confirm Password</Form.Label>
              <div className="password-icon-wrapper">
                <Form.Control
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  placeholder="Enter confirm password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  maxLength={128}
                  required
                />
                <span className="eye-btn"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <i className="bi bi-eye-slash"></i> : <i className="bi bi-eye"></i>}
                </span>
              </div>
            </Form.Group>
          </Col>
        </Row>

        <div className="cover-upload-section mb-3">
          <Form.Label>Upload Profile Cover</Form.Label>
          <Form.Control type="file" hidden ref={coverInputRef} onChange={(e) => handleImageChange(e, 'cover')} accept="image/*" />
          <div className="cover-image-preview d-flex align-items-center justify-content-center overflow-hidden position-relative pointer rounded" onClick={() => coverInputRef.current.click()} >
            {coverImage ? (
              <img src={coverImage} alt="Cover" className="h-100 w-100 object-fit-cover" />
            ) : (
              <div className="text-center text-muted">
                <div className="icon mb-1"><i className="bi bi-plus-circle-dotted text-xl"></i></div>
                <div className="small fw-medium">Add cover banner</div>
              </div>
            )}
            {coverImage && <div className="upload-overlay">Change Banner</div>}
          </div>
        </div>

        <Form.Group className="mb-4">
          <Form.Label>Bio Information</Form.Label>
          <Form.Control as="textarea" name="description" rows={3} placeholder="Tell us a little about yourself ..." value={formData.description} onChange={handleInputChange} />
        </Form.Group>

        <Button
          variant="primary"
          type="submit"
          className="w-100 btn-custom"
          disabled={!isFormValid || loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" />
              Creating Account...
            </>
          ) : `Create ${role} Account`}
        </Button>

        <div className="text-center my-3 text-muted text-sm">
          <span>or sign up with</span>
        </div>

        <div className="d-flex justify-content-center mb-3">
          <div id="google-signup-btn"></div>
        </div>

        <div className="auth-footer">
          Already have an account? <Link to="/login">Sign In</Link>
        </div>
      </Form>
    </AuthLayout>
  );
};

export default SignUp;
